/**
 * Top-level layout: toolbar across the top, graph canvas on
 * the left, Play-only RightPanel on the right. Scenes are
 * edited inline on the graph; adventure-wide settings live in
 * a modal (AdventureSettingsDialog).
 *
 * State held here:
 *   - `json`           — current AdventureJson
 *   - `jsonVersion`    — counter, bumped on structural events
 *                        (load, new, start-scene change). Used
 *                        as the ReactFlow `key` so the canvas
 *                        remounts and fitView re-runs.
 *   - `selectedScene`  — id of the selected scene node, or null
 *   - `error`          — last error message from a load action
 *
 * The graph runs in *controlled* mode (`useNodesState` /
 * `useEdgesState`). Without the change handlers React Flow
 * can't propagate selection state to all nodes — some clicks
 * visibly do nothing. With them, edits to `json` flow into the
 * graph via a useEffect that rebuilds the nodes/edges arrays,
 * and the canvas updates in place without losing the user's
 * pan/zoom or selection.
 *
 * Live editing path:
 *   user edits a field in the panel
 *     → onJsonChange called with updated AdventureJson
 *     → setJson(next)
 *     → useEffect rebuilds graph nodes/edges, setRfNodes runs
 *     → ReactFlow re-renders the canvas in place (no remount)
 *
 * Structural path (load / new / change start scene):
 *   user clicks load/new
 *     → setJson(next) + bumpVersion()
 *     → key={jsonVersion} change forces ReactFlow remount
 *     → fitView re-runs focused on start + successors
 */
import { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import {
	ReactFlow,
	ReactFlowProvider,
	Background,
	Controls,
	MiniMap,
	type Node,
	type Edge,
	type Connection,
	type OnSelectionChangeParams,
	type FinalConnectionState,
	BackgroundVariant,
	useNodesState,
	useEdgesState,
	useReactFlow
} from '@xyflow/react';
import '@xyflow/react/dist/style.css';

/**
 * Zoom config.
 *
 * `DEFAULT_ZOOM` is the exact level fitView lands at on load —
 * both min and max in fitViewOptions are set to this same
 * value so React Flow can't pick anything else when it
 * auto-fits. (Previous incarnation set min=1.056, max=1.6 —
 * fitView's "natural" fit for the start + successors was
 * above 1.6, so it clamped to the max ceiling instead of the
 * tuned floor.)
 *
 * `CANVAS_MIN_ZOOM` / `CANVAS_MAX_ZOOM` are the wider bounds
 * the user can scroll-wheel / button-zoom INSIDE after the
 * initial fit — they don't affect the default view.
 */
const DEFAULT_ZOOM = 1.056;
const CANVAS_MIN_ZOOM = 0.2;
const CANVAS_MAX_ZOOM = 2;

import { Toolbar } from './components/Toolbar';
import { RightPanel } from './components/RightPanel';
import { AdventureSettingsDialog } from './components/AdventureSettingsDialog';
import { SceneNode } from './components/SceneNode';
import { FinishNode } from './components/FinishNode';
import { ChoiceEdge } from './components/ChoiceEdge';
import { LoadDialog } from './components/LoadDialog';
import { buildGraph, FINISH_NODE_ID, ARROW_MARKER_ID } from './lib/graph';
import { layoutGraph } from './lib/layout';
import { computeMaxScore } from 'console-adventure';
import { EXAMPLE_ADVENTURE, FOUNDRY_EXAMPLE } from './lib/examples';
import { BLANK_ADVENTURE, STARTER_ADVENTURE } from './lib/blank';
import {
	createSave,
	updateSave,
	loadSave,
	storageAvailable,
	saveDraft,
	loadDraft,
	clearDraft
} from './lib/storage';
import { DraftBanner } from './components/DraftBanner';
import { Toast } from './components/Toast';
import { CommandPalette, type PaletteCommand } from './components/CommandPalette';
import { updateChoice, addSceneFromChoice, deleteScene } from './lib/edit';
import { BootOverlay, shouldShowBootOverlay } from './components/BootOverlay';
import { Tour, clearTourSeen } from './components/Tour';
import { AboutDialog } from './components/AboutDialog';
import { ValidationNote } from './components/ValidationNote';
import { validate } from './lib/validate';
import { ShipDialog } from './components/ShipDialog';
import { InlineSceneEditor } from './components/InlineSceneEditor';
import { EdgeEditor } from './components/EdgeEditor';
import { ConfirmProvider, useConfirm } from './lib/confirm';
import { PromptProvider, usePrompt } from './lib/prompt';
import { SceneActionsProvider } from './lib/sceneActions';
import { ScriptView } from './components/ScriptView';
import { ViewToggle } from './components/ViewToggle';
import { FlowDirectionToggle } from './components/FlowDirectionToggle';
import { loadViewMode, saveViewMode, type ViewMode } from './lib/viewMode';
import {
	loadFlowDirection,
	saveFlowDirection,
	type FlowDirection
} from './lib/flowDirection';
import type { PlayState } from './components/Terminal';

const EMPTY_PLAYSTATE: PlayState = {
	sceneId: null,
	finished: false,
	score: 0,
	visited: [],
	takenEdges: []
};
import { VOID, PHOSPHOR, MAGENTA, AMBER, DIM, PANEL, PANEL_BORDER } from './lib/theme';

/**
 * Custom arrow marker.
 *
 * Geometry:
 *   - Triangle path: M 0 0 L 10 5 L 0 10 z — tip at x=10
 *   - refX="10" — the tip is the anchor point that sits on
 *     the line endpoint. The base (x=0) extends backward
 *     along the line, toward the source node, into the gap
 *     between nodes. So the visible arrow sits in the
 *     inter-node gap, tip kissing the target node's handle.
 *   - markerWidth/Height kept small so the triangle's
 *     interior is barely wider than the edge stroke — the
 *     line and the triangle visually merge along the line's
 *     axis without an obvious "line stabbing the arrow"
 *     artifact.
 *
 * Earlier incarnations:
 *   - React Flow built-in `arrowclosed` was the same geometry
 *     but at default size, which made the interior wide enough
 *     that the line through the centre was very visible.
 *   - Custom with refX=1 had the triangle extending FORWARD
 *     past the line endpoint, which put it inside the target
 *     node's area — node z-order covered the triangle and
 *     made the arrows look like they disappeared.
 */
function MarkerDefs() {
	return (
		<svg
			aria-hidden="true"
			style={{
				position: 'absolute',
				width: 0,
				height: 0,
				overflow: 'hidden',
				pointerEvents: 'none'
			}}
		>
			<defs>
				<marker
					id={ARROW_MARKER_ID}
					viewBox="0 0 10 10"
					refX="10"
					refY="5"
					markerWidth="9"
					markerHeight="9"
					orient="auto"
				>
					<path d="M 0 0 L 10 5 L 0 10 z" fill={AMBER} />
				</marker>
			</defs>
		</svg>
	);
}
import type { AdventureJson } from 'console-adventure';

const nodeTypes = { scene: SceneNode, finish: FinishNode };
// Custom edge types. Lifted to module scope (matches nodeTypes
// pattern above) so React Flow doesn't see a new reference on
// every render and complain via its console warning.
const edgeTypes = { choice: ChoiceEdge };

/**
 * Outermost wrapper. The Provider is mounted here so any
 * descendant — including the inline scene editor portal — can
 * call `useReactFlow()` to get hold of the canvas instance and
 * convert flow coordinates to screen coordinates. Without it,
 * `useReactFlow()` throws "Zustand provider not found."
 */
export default function App() {
	return (
		<ConfirmProvider>
			<PromptProvider>
				<ReactFlowProvider>
					<AppInner />
				</ReactFlowProvider>
			</PromptProvider>
		</ConfirmProvider>
	);
}

function AppInner() {
	const confirm = useConfirm();
	const prompt = usePrompt();
	// Restore the draft if one exists -- this lets a tab refresh
	// preserve in-progress unsaved work. The decision is captured
	// once at mount (useState initialiser) so a fresh draft
	// written by the autosave effect below doesn't retrigger
	// "Draft restored" on every render.
	const [initialFromDraft] = useState(() => loadDraft());
	const [json, setJson] = useState<AdventureJson>(
		initialFromDraft?.json ?? EXAMPLE_ADVENTURE
	);
	const [draftRestored, setDraftRestored] = useState<string | null>(
		initialFromDraft?.savedAt ?? null
	);
	const [jsonVersion, setJsonVersion] = useState(0);
	const [selectedScene, setSelectedScene] = useState<string | null>(null);
	const [error, setError] = useState<string | null>(null);
	const [showLoadDialog, setShowLoadDialog] = useState(false);
	const [showShipDialog, setShowShipDialog] = useState(false);
	const [currentSaveName, setCurrentSaveName] = useState<string | null>(null);
	// Track the localStorage save id alongside the name. Without
	// it, every save would CREATE a new entry (the original
	// behaviour), so saving twice with the same name produced
	// two duplicate rows in the load dialog. Tracking the id lets
	// "save" update-in-place; new entries only spawn from
	// explicit "save as" / "duplicate" actions.
	const [currentSaveId, setCurrentSaveId] = useState<string | null>(null);
	// "Document is dirty" — set on every json edit; cleared on
	// save / load / new. Drives the saved-state dot in the
	// Toolbar. Lives in state rather than ref so the dot
	// re-renders when it flips.
	// A restored draft IS, by definition, unsaved -- seed the
	// dirty flag accordingly so the saved-state dot starts amber
	// and the autosave effect keeps writing the draft as the
	// user resumes editing.
	const [dirty, setDirty] = useState(initialFromDraft !== null);
	// Boot overlay: shown once per browser unless the user
	// ticks "don't show on boot" inside it. The persisted skip
	// flag lives under `cas:skipBoot` — checked by
	// `shouldShowBootOverlay()` so the read is encapsulated.
	const [showBoot, setShowBoot] = useState<boolean>(() => shouldShowBootOverlay());
	// Guided tour. Triggered by the boot overlay's "Take the
	// tour" choice and by the Toolbar ⋯ → Help/tour menu
	// entry. Component handles its own seen-state persistence.
	const [showTour, setShowTour] = useState(false);
	const [showAbout, setShowAbout] = useState(false);
	// ⌘K command palette. Open via the keyboard shortcut below
	// and via the ⋯ menu entry; closes via Esc / backdrop /
	// running a command.
	const [showPalette, setShowPalette] = useState(false);
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (!(e.metaKey || e.ctrlKey) || e.key.toLowerCase() !== 'k') return;
			e.preventDefault();
			setShowPalette((open) => !open);
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);
	// Adventure-level settings modal (start / tiers / items /
	// share / export). Replaces the old right-panel Inspect tab
	// for adventure-wide config.
	const [showSettings, setShowSettings] = useState(false);

	// Move 03 — Playtest companion. The Terminal owns the actual
	// `Adventure` instance; it surfaces live state up through
	// `onStateChange` so the graph can mirror the run. We hold a
	// `playFrom` override here so "▶ play from here" buttons on
	// the inline editor can boot the playtest mid-graph without
	// mutating the document's `start`.
	const [playState, setPlayState] = useState<PlayState>(EMPTY_PLAYSTATE);
	const [playFrom, setPlayFrom] = useState<string | null>(null);
	const [playRequestId, setPlayRequestId] = useState(0);

	// Move 04 — top-level view mode (graph / write / split).
	// Persisted to localStorage so the author lands back in
	// whichever surface they were last in. Defaults to graph so
	// existing users see nothing change.
	const [viewMode, setViewMode] = useState<ViewMode>(() => loadViewMode());
	const handleViewModeChange = useCallback((m: ViewMode) => {
		setViewMode(m);
		saveViewMode(m);
	}, []);

	// Flow direction (horizontal / vertical). Horizontal is the
	// default because the per-row source handles sit on the
	// right edge of each card -- pairing them with a left-edge
	// target on the next node gives clean wires. Vertical keeps
	// the legacy top-down layout for authors who prefer it.
	// Both branches structurally remount the graph (jsonVersion
	// bump) so fitView re-frames on the new orientation.
	const [flowDirection, setFlowDirection] = useState<FlowDirection>(() =>
		loadFlowDirection()
	);

	// Per-scene position overrides. Populated by drop-to-create
	// (handleConnectEnd captures the cursor's flow-space
	// coordinates and pins the new scene there), cleared by the
	// auto-layout button so the user can always tidy back to
	// the BFS grid.
	const [manualPositions, setManualPositions] = useState<
		Map<string, { x: number; y: number }>
	>(() => new Map());

	// Undo / redo. `past` holds PRE-change snapshots; `future`
	// holds states the user undid past. Both capped at
	// HISTORY_CAP entries so the stack can't grow unbounded
	// during a long session.
	const HISTORY_CAP = 100;
	const HISTORY_COALESCE_MS = 600;
	interface HistoryEntry {
		json: AdventureJson;
	}
	const [history, setHistory] = useState<{
		past: HistoryEntry[];
		future: HistoryEntry[];
	}>({ past: [], future: [] });
	// Pre-change snapshot reference. Updated synchronously when
	// we apply undo / redo so the debounced snapshot effect
	// doesn't fire spuriously on the very next render.
	const previousJsonRef = useRef<AdventureJson>(json);
	// Snapshot on a debounce so a burst of keystrokes turns
	// into ONE history entry rather than one per character. The
	// snapshot taken is the PREVIOUS json -- undo restores the
	// state from before this edit, not after.
	useEffect(() => {
		const t = setTimeout(() => {
			if (previousJsonRef.current !== json) {
				setHistory((h) => ({
					past: [...h.past, { json: previousJsonRef.current }].slice(
						-HISTORY_CAP
					),
					future: []
				}));
				previousJsonRef.current = json;
			}
		}, HISTORY_COALESCE_MS);
		return () => clearTimeout(t);
	}, [json]);

	const undo = useCallback(() => {
		setHistory((h) => {
			if (h.past.length === 0) return h;
			const last = h.past[h.past.length - 1];
			setJson(last.json);
			previousJsonRef.current = last.json;
			setJsonVersion((v) => v + 1);
			return {
				past: h.past.slice(0, -1),
				future: [{ json }, ...h.future].slice(0, HISTORY_CAP)
			};
		});
	}, [json]);

	const redo = useCallback(() => {
		setHistory((h) => {
			if (h.future.length === 0) return h;
			const next = h.future[0];
			setJson(next.json);
			previousJsonRef.current = next.json;
			setJsonVersion((v) => v + 1);
			return {
				past: [...h.past, { json }].slice(-HISTORY_CAP),
				future: h.future.slice(1)
			};
		});
	}, [json]);

	const clearHistory = useCallback(() => {
		setHistory({ past: [], future: [] });
		previousJsonRef.current = json;
	}, [json]);

	// Keyboard shortcuts. Cmd/Ctrl+Z = undo; Cmd/Ctrl+Shift+Z
	// or Cmd/Ctrl+Y = redo. Skipped when focus is in an
	// input/textarea so the browser's built-in text-edit undo
	// still works inside the inline editor's fields.
	const undoRef = useRef<() => void>(() => {});
	const redoRef = useRef<() => void>(() => {});
	useEffect(() => {
		undoRef.current = undo;
		redoRef.current = redo;
	}, [undo, redo]);
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (!(e.metaKey || e.ctrlKey)) return;
			const target = e.target as HTMLElement | null;
			const tag = target?.tagName;
			if (
				tag === 'INPUT' ||
				tag === 'TEXTAREA' ||
				tag === 'SELECT' ||
				target?.isContentEditable
			) {
				return;
			}
			const k = e.key.toLowerCase();
			if (k === 'z' && !e.shiftKey) {
				e.preventDefault();
				undoRef.current();
			} else if ((k === 'z' && e.shiftKey) || k === 'y') {
				e.preventDefault();
				redoRef.current();
			}
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);
	const handleFlowDirectionChange = useCallback((d: FlowDirection) => {
		setFlowDirection(d);
		saveFlowDirection(d);
		setJsonVersion((v) => v + 1);
	}, []);

	/**
	 * "▶ play from here" — boot the playtest at a specific
	 * scene. The right panel (Play-only) is always visible in
	 * graph mode, so there's no tab to switch — just set the
	 * override + bump the request id so Terminal's rebuild
	 * effect re-fires even on a repeat click of the same node
	 * (React's equality check on `playFrom` alone would skip it).
	 */
	const handlePlayFromHere = useCallback((sceneId: string) => {
		setPlayFrom(sceneId);
		setPlayRequestId((n) => n + 1);
	}, []);
	// Pre-compute lookup sets to keep the graph-rebuild useEffect
	// O(1) per node when it threads play flags through.
	const visitedSet = useMemo(() => new Set(playState.visited), [playState.visited]);
	const takenEdgeSet = useMemo(() => new Set(playState.takenEdges), [playState.takenEdges]);
	const liveSceneId = playState.sceneId;

	// Whether localStorage is usable in this browsing context.
	// Resolved once on first render; passed through to the
	// toolbar so save / load fade rather than silently no-op
	// in private browsing / storage-blocked contexts.
	const saveAvailable = useMemo(() => storageAvailable(), []);

	const maxScore = useMemo(() => computeMaxScore(json), [json]);
	// Same algorithm graph.ts and the ship dialog use. Memoised
	// here so the ValidationNote chip can read it without
	// re-running the BFS on every render.
	const validation = useMemo(() => validate(json), [json]);

	// Autosave to the single-slot draft. Debounced 600ms so
	// mid-word keystrokes don't thrash localStorage. Gated on
	// `dirty` so the initial unmodified bootstrap state (the
	// example, fresh new, just-loaded save) doesn't get
	// auto-promoted to a draft -- otherwise a no-edit refresh
	// would falsely show "Draft restored." Cleared on explicit
	// lifecycle transitions elsewhere in this file.
	useEffect(() => {
		if (!dirty) return;
		const t = setTimeout(() => saveDraft(json), 600);
		return () => clearTimeout(t);
	}, [json, dirty]);

	// Seed useNodesState / useEdgesState with the computed
	// graph on the FIRST render. Previously they started as
	// empty arrays and got populated by a useEffect — but
	// React Flow's `fitView` prop fires once on mount, and an
	// empty-nodes fit lands on whatever React Flow's fallback
	// viewport happens to be (NOT on fitViewOptions). When the
	// real nodes arrived via setRfNodes a tick later, fitView
	// was already done, so the zoom got stuck at the fallback.
	// `useMemo` with empty deps captures the initial json /
	// maxScore at mount; `key={jsonVersion}` remounts to refresh
	// on structural changes.
	// eslint-disable-next-line react-hooks/exhaustive-deps
	const initialGraph = useMemo(() => {
		const built = buildGraph(json, maxScore, undefined, flowDirection);
		return {
			// On first render manualPositions is empty by construction;
		// the explicit pass keeps the call signature consistent
		// with the in-place rebuilds below.
		nodes: layoutGraph(built.nodes, built.edges, json.start, flowDirection, manualPositions),
			edges: built.edges
		};
	}, []);

	const [rfNodes, setRfNodes, onNodesChange] = useNodesState<Node>(
		initialGraph.nodes
	);
	const [rfEdges, setRfEdges, onEdgesChange] = useEdgesState<Edge>(
		initialGraph.edges
	);

	// Sync into React Flow's state on subsequent json edits or
	// play-state changes (neither bumps jsonVersion / causes a
	// remount). Play-state changes flow `isLive` / `isVisited`
	// into each node's data, and re-style edges so the taken
	// path renders cyan — purely visual, no layout impact.
	const isFirstRenderRef = useRef(true);
	useEffect(() => {
		if (isFirstRenderRef.current) {
			isFirstRenderRef.current = false;
			return;
		}
		const built = buildGraph(
			json,
			maxScore,
			{
				liveSceneId,
				visited: visitedSet,
				takenEdges: takenEdgeSet
			},
			flowDirection
		);
		const positioned = layoutGraph(
			built.nodes,
			built.edges,
			json.start,
			flowDirection,
			manualPositions
		);
		// Carry over React Flow's `selected` flag from the
		// previous nodes by id. Without this, every json edit
		// (every keystroke in the heading / narration / etc.)
		// drops fresh node objects with no selection state in —
		// React Flow then fires `onSelectionChange` with an
		// empty array, which sets selectedScene to null and
		// bounces the user out of the editor view.
		setRfNodes((prev) => {
			const wasSelected = new Map(prev.map((n) => [n.id, !!n.selected]));
			return positioned.map((n) =>
				wasSelected.get(n.id) ? { ...n, selected: true } : n
			);
		});
		setRfEdges(built.edges);
	}, [
		json,
		maxScore,
		liveSceneId,
		visitedSet,
		takenEdgeSet,
		flowDirection,
		manualPositions,
		setRfNodes,
		setRfEdges
	]);

	const focusNodeIds = useMemo(() => {
		const successors =
			json.scenes[json.start]?.choices
				.map((c) => c.next)
				.filter((n): n is string => n !== null) ?? [];
		return Array.from(new Set([json.start, ...successors]));
	}, [json]);

	const handleSelectionChange = useCallback(
		(params: OnSelectionChangeParams) => {
			// `selectedScene` / `selectedEdgeId` drive the inline
			// editor card + edge editor, which only make sense
			// with a single selection. With multiple selected we
			// fall back to null so the editors close and the user
			// can shape the multi-selection without floating UI
			// fighting for screen space.
			const sceneNodes = params.nodes.filter(
				(n) => n.id !== FINISH_NODE_ID
			);
			if (sceneNodes.length === 1) {
				setSelectedScene(sceneNodes[0].id);
			} else {
				setSelectedScene(null);
			}
			if (params.edges.length === 1) {
				setSelectedEdgeId(params.edges[0].id);
			} else {
				setSelectedEdgeId(null);
			}
		},
		[]
	);

	// React Flow's `onNodeClick` fires with the post-click node
	// state (`selected: true` regardless of whether selection
	// actually changed), so we can't tell from `node.selected`
	// alone whether the user clicked an already-selected node.
	// This ref tracks the selection AS OF THE PREVIOUS RENDER —
	// when onNodeClick fires synchronously during a click, the
	// ref still reflects the pre-click state. If it equals the
	// clicked node's id, the user clicked the same node twice in
	// a row → deselect.
	const prevSelectedRef = useRef<string | null>(null);
	useEffect(() => {
		prevSelectedRef.current = selectedScene;
	}, [selectedScene]);

	// Track which edge (if any) is currently selected — mirrors
	// `selectedScene` for the nodes side. We need our own copy
	// (rather than reading from rfEdges every time) because the
	// delete shortcut needs synchronous access to the choice
	// coords for the confirmation prompt.
	const [selectedEdgeId, setSelectedEdgeId] = useState<string | null>(null);
	const prevSelectedEdgeRef = useRef<string | null>(null);
	useEffect(() => {
		prevSelectedEdgeRef.current = selectedEdgeId;
	}, [selectedEdgeId]);

	// Keyboard shortcut: Shift+L re-runs the auto layout. Skips
	// when the user is typing in an input/textarea so the
	// shortcut doesn't fight the inline editor's heading field
	// (capital L is a common letter to type).
	const handleAutoLayoutRef = useRef<() => void>(() => {});
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'L' || !e.shiftKey || e.ctrlKey || e.metaKey || e.altKey) return;
			const target = e.target as HTMLElement | null;
			const tag = target?.tagName;
			if (
				tag === 'INPUT' ||
				tag === 'TEXTAREA' ||
				tag === 'SELECT' ||
				target?.isContentEditable
			) {
				return;
			}
			e.preventDefault();
			handleAutoLayoutRef.current();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	// Keyboard shortcut: Delete / Backspace deletes the
	// currently-selected scene, with a confirmation. Skips when
	// focus is in any editable field — Backspace inside the
	// heading textarea must keep deleting characters, not
	// torching the scene the author is typing into.
	const handleDeleteSelectedRef = useRef<() => void>(() => {});
	useEffect(() => {
		const onKey = (e: KeyboardEvent) => {
			if (e.key !== 'Delete' && e.key !== 'Backspace') return;
			const target = e.target as HTMLElement | null;
			const tag = target?.tagName;
			if (
				tag === 'INPUT' ||
				tag === 'TEXTAREA' ||
				tag === 'SELECT' ||
				target?.isContentEditable
			) {
				return;
			}
			e.preventDefault();
			handleDeleteSelectedRef.current();
		};
		window.addEventListener('keydown', onKey);
		return () => window.removeEventListener('keydown', onKey);
	}, []);

	const handleNodeClick = useCallback(
		(e: React.MouseEvent, node: Node) => {
			// Skip the click-to-deselect toggle when a modifier
			// is held -- the user is multi-selecting, not
			// trying to clear the previous pick.
			if (e.shiftKey || e.metaKey || e.ctrlKey) return;
			if (prevSelectedRef.current === node.id) {
				// Toggle off — clear the selected flag on every
				// node so React Flow's `onSelectionChange` fires
				// with an empty array, which threads through to
				// setSelectedScene(null) via handleSelectionChange.
				setRfNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
			}
		},
		[setRfNodes]
	);

	/**
	 * Edge click toggle — same shape as the node-click toggle.
	 * If the user clicked the same edge twice in a row, clear
	 * every edge's `selected` flag and reset our tracking state.
	 * Otherwise let the default selection settle in: the click
	 * sets `selected: true` on the clicked edge, and our
	 * onSelectionChange handler picks it up.
	 */
	const handleEdgeClick = useCallback(
		(e: React.MouseEvent, edge: Edge) => {
			// Skip the toggle on modifier-clicks -- user is
			// multi-selecting.
			if (e.shiftKey || e.metaKey || e.ctrlKey) return;
			if (prevSelectedEdgeRef.current === edge.id) {
				setRfEdges((prev) => prev.map((edg) => ({ ...edg, selected: false })));
				setSelectedEdgeId(null);
			}
		},
		[setRfEdges]
	);

	/**
	 * Select the edge belonging to a given (scene, choice) and
	 * deselect the scene node — handing off from the inline
	 * scene card to the connection editor. Wired to the ✎
	 * button on each inline choice row so reaction text + item
	 * gating (which live on the EdgeEditor) are one click away.
	 *
	 * Every choice always has a corresponding edge — terminal
	 * choices wire to the synthetic finish node — so the lookup
	 * by `data.{sceneId,choiceIndex}` always resolves.
	 */
	const handleEditConnection = useCallback(
		(sceneId: string, choiceIndex: number) => {
			const edge = rfEdges.find((e) => {
				const d = e.data as
					| { sceneId?: string; choiceIndex?: number }
					| undefined;
				return d?.sceneId === sceneId && d?.choiceIndex === choiceIndex;
			});
			if (!edge) return;
			// Drop the node selection (closes the inline card) and
			// select the edge (opens the EdgeEditor).
			setSelectedScene(null);
			setRfNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
			setRfEdges((prev) =>
				prev.map((e) => ({ ...e, selected: e.id === edge.id }))
			);
			setSelectedEdgeId(edge.id);
		},
		[rfEdges, setRfNodes, setRfEdges]
	);

	/**
	 * Drive the selection / view state each guided-tour step
	 * needs so its highlight target actually exists:
	 *   - scene      → select the start node (inline card open)
	 *   - connection → open the start scene's first connection
	 *                  editor (the EdgeEditor the step highlights)
	 *   - everything else → clear selection so no floating card
	 *                  overlaps the toolbar / sub-bar / panel
	 *                  targets.
	 * Called by the Tour on every step change, so back / forward
	 * navigation restores the right state cleanly.
	 */
	const clearAllSelection = useCallback(() => {
		setSelectedScene(null);
		setSelectedEdgeId(null);
		setRfNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
		setRfEdges((prev) => prev.map((e) => ({ ...e, selected: false })));
	}, [setRfNodes, setRfEdges]);

	const handleTourStep = useCallback(
		(id: string) => {
			// Select a scene node (opens the inline card). Used by
			// the scene step (start) and the items step (an
			// item-bearing scene, so the ITEMS HERE section shows).
			const selectScene = (sceneId: string) => {
				setSelectedEdgeId(null);
				setRfEdges((prev) => prev.map((e) => ({ ...e, selected: false })));
				setSelectedScene(sceneId);
				setRfNodes((prev) =>
					prev.map((n) => ({ ...n, selected: n.id === sceneId }))
				);
			};
			if (id === 'scene') {
				selectScene(json.start);
			} else if (id === 'items') {
				// Prefer a scene that actually has items so the
				// ITEMS HERE section is visible to point at; fall
				// back to start otherwise (adventure with no items —
				// the step's fallback card still explains the idea).
				const itemScene =
					Object.keys(json.scenes).find(
						(sid) => (json.scenes[sid].items?.length ?? 0) > 0
					) ?? json.start;
				selectScene(itemScene);
			} else if (id === 'connection') {
				handleEditConnection(json.start, 0);
			} else {
				clearAllSelection();
			}
		},
		[json.start, json.scenes, handleEditConnection, clearAllSelection, setRfNodes, setRfEdges]
	);

	/**
	 * Pin the node at the position the user dragged it to so
	 * the next layoutGraph rebuild doesn't snap it back to the
	 * BFS grid. Same `manualPositions` map drop-to-create
	 * populates -- "Auto layout" (Shift+L) is the universal
	 * "tidy back to the grid" reset for both.
	 */
	const handleNodeDragStop = useCallback(
		(_e: React.MouseEvent, node: Node) => {
			if (node.id === FINISH_NODE_ID) return;
			setManualPositions((curr) => {
				const next = new Map(curr);
				next.set(node.id, { x: node.position.x, y: node.position.y });
				return next;
			});
		},
		[]
	);

	// React Flow instance — used by the auto-layout button to
	// re-frame the camera after positions get reset. Safe to
	// call here because AppInner sits inside <ReactFlowProvider>
	// (see the default export above).
	const reactFlow = useReactFlow();

	/**
	 * Camera follow-along during playtest. Whenever the live
	 * scene id changes (start, choose, restart), tween the
	 * camera to centre on the new node so the author doesn't
	 * have to pan to find what just lit up.
	 *
	 * Preserves the user's current zoom so the tween doesn't
	 * fight a wider/narrower view they've deliberately chosen.
	 * 500ms duration matches React Flow's own fitView default
	 * and reads as "smoothly following" without feeling sluggish.
	 *
	 * Skipped in write mode (no canvas), and when the live
	 * scene's node isn't on screen yet (e.g. mid-rebuild after
	 * a structural edit -- the next render will retrigger).
	 */
	useEffect(() => {
		if (!liveSceneId) return;
		if (viewMode === 'write') return;
		const node = reactFlow.getNode(liveSceneId);
		if (!node) return;
		// Node position is the top-left in flow space; we want
		// the centre. SceneNode is a fixed 300px wide; height
		// varies with choice count but ~140 is a workable
		// midpoint for the framing.
		const NODE_WIDTH = 300;
		const NODE_HEIGHT_ESTIMATE = 140;
		reactFlow.setCenter(
			node.position.x + NODE_WIDTH / 2,
			node.position.y + NODE_HEIGHT_ESTIMATE / 2,
			{
				duration: 500,
				zoom: reactFlow.getZoom()
			}
		);
	}, [liveSceneId, viewMode, reactFlow]);

	/**
	 * Re-run layoutGraph and overwrite every node's position.
	 * Useful after the user has hand-dragged nodes around the
	 * canvas — React Flow lets them park nodes wherever, but
	 * there's no built-in "tidy up" action. This brings the
	 * graph back to the BFS-derived top-down layout.
	 *
	 * We preserve the `selected` flag and don't bump
	 * `jsonVersion`, so the canvas re-positions in place rather
	 * than remounting and losing the selection.
	 */
	const handleAutoLayout = useCallback(() => {
		// "Tidy up" means going back to the BFS-derived grid, so
		// drop any pins from drop-to-create. Pass an empty map
		// directly (the state update is async; future rebuilds
		// pick up the new empty state).
		setManualPositions(new Map());
		const built = buildGraph(
			json,
			maxScore,
			{
				liveSceneId,
				visited: visitedSet,
				takenEdges: takenEdgeSet
			},
			flowDirection
		);
		const positioned = layoutGraph(
			built.nodes,
			built.edges,
			json.start,
			flowDirection
		);
		setRfNodes((prev) => {
			const wasSelected = new Map(prev.map((n) => [n.id, !!n.selected]));
			return positioned.map((n) =>
				wasSelected.get(n.id) ? { ...n, selected: true } : n
			);
		});
		setRfEdges(built.edges);
		// Defer fitView so the new positions have committed
		// before the camera reads them. Without the
		// requestAnimationFrame the fitView lands on the OLD
		// positions and the visible tidy-up feels half-finished.
		requestAnimationFrame(() => {
			reactFlow.fitView({
				padding: 0.15,
				duration: 400,
				minZoom: DEFAULT_ZOOM,
				maxZoom: DEFAULT_ZOOM
			});
		});
	}, [
		json,
		maxScore,
		liveSceneId,
		visitedSet,
		takenEdgeSet,
		flowDirection,
		setRfNodes,
		setRfEdges,
		reactFlow
	]);

	// Keep the keyboard shortcut's ref pointing at the latest
	// closure — the listener itself only mounts once.
	useEffect(() => {
		handleAutoLayoutRef.current = handleAutoLayout;
	}, [handleAutoLayout]);

	/**
	 * Delete the currently-selected scene, with a confirmation.
	 * Bailout cases — silently no-op rather than nag:
	 *   - No scene is selected.
	 *   - The selected scene is the document's start (the engine
	 *     would have nowhere to begin). The inline editor's
	 *     delete button shows a "set start first" hint for this
	 *     case; from the keyboard there's no UI surface to
	 *     explain it, so silence is the right call.
	 *
	 * Also clears the selection so the inline editor unmounts
	 * cleanly after deletion.
	 */
	const handleDeleteSelected = useCallback(async () => {
		// Pull ALL selected nodes + edges from React Flow's
		// state, not just the "primary" single-selection ids.
		// With multi-select enabled the user might have shift-
		// dragged across 3 scenes + 4 edges; we need to delete
		// the whole pick in one atomic json mutation.
		const selectedNodes = rfNodes.filter(
			(n) => n.selected && n.id !== FINISH_NODE_ID
		);
		const selectedEdges = rfEdges.filter((e) => e.selected);

		// Edges take priority when no nodes are selected; if
		// both kinds are picked we delete the scenes (which
		// cascades to those edges anyway) and leave standalone
		// edge picks alone.
		if (selectedNodes.length === 0 && selectedEdges.length === 0) return;

		if (selectedNodes.length === 0 && selectedEdges.length > 0) {
			// Pure-edge selection: rewire each choice's `next` to
			// null. The choices themselves stay so the author
			// keeps their labels + points.
			const targets: Array<{ sceneId: string; choiceIndex: number; edgeId: string }> = [];
			for (const edge of selectedEdges) {
				const data = edge.data as
					| { sceneId?: string; choiceIndex?: number }
					| undefined;
				if (data?.sceneId === undefined || data.choiceIndex === undefined) continue;
				targets.push({
					sceneId: data.sceneId,
					choiceIndex: data.choiceIndex,
					edgeId: edge.id
				});
			}
			if (targets.length === 0) return;
			const ok = await confirm({
				title:
					targets.length === 1 ? 'Delete connection' : `Delete ${targets.length} connections`,
				message:
					targets.length === 1 ? (
						<>
							Remove this connection? The choice stays in its scene but is
							rewired to <strong>finish</strong> (null).
						</>
					) : (
						<>
							Remove <strong>{targets.length}</strong> connections? Each choice
							stays in its scene but is rewired to <strong>finish</strong>{' '}
							(null).
						</>
					),
				confirmLabel: 'Delete',
				tone: 'danger'
			});
			if (!ok) return;
			let next = json;
			for (const { sceneId, choiceIndex } of targets) {
				next = updateChoice(next, sceneId, choiceIndex, { next: null });
			}
			setJson(next);
			setRfEdges((prev) => prev.map((e) => ({ ...e, selected: false })));
			setSelectedEdgeId(null);
			return;
		}

		// Otherwise we're deleting scenes. Filter out the start
		// scene (engine refuses to remove it -- it'd have
		// nowhere to begin). Bail silently if all picks were
		// the start scene; show a tailored toast otherwise.
		const deletable = selectedNodes.filter((n) => n.id !== json.start);
		const skippedStart = selectedNodes.length - deletable.length;
		if (deletable.length === 0) {
			if (skippedStart > 0) {
				setError(
					'Can\'t delete the start scene. Set a different start in the inspector first.'
				);
			}
			return;
		}
		const names = deletable.map((n) => n.id);
		const ok = await confirm({
			title:
				deletable.length === 1
					? 'Delete scene'
					: `Delete ${deletable.length} scenes`,
			message:
				deletable.length === 1 ? (
					<>
						Delete scene <strong>{names[0]}</strong>? Any choices pointing here
						will be rewired to <strong>finish</strong> (null).
						{skippedStart > 0 && (
							<>
								<br />
								<span style={{ opacity: 0.7 }}>
									(The start scene was also selected and will be skipped.)
								</span>
							</>
						)}
					</>
				) : (
					<>
						Delete <strong>{deletable.length}</strong> scenes (
						<code style={{ fontSize: 10 }}>{names.join(', ')}</code>)? Any
						choices pointing at any of them will be rewired to{' '}
						<strong>finish</strong> (null).
						{skippedStart > 0 && (
							<>
								<br />
								<span style={{ opacity: 0.7 }}>
									(The start scene was also selected and will be skipped.)
								</span>
							</>
						)}
					</>
				),
			confirmLabel: 'Delete',
			tone: 'danger'
		});
		if (!ok) return;
		let next = json;
		for (const node of deletable) {
			next = deleteScene(next, node.id);
		}
		setJson(next);
		setSelectedScene(null);
		setRfNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
		setRfEdges((prev) => prev.map((e) => ({ ...e, selected: false })));
	}, [
		rfNodes,
		rfEdges,
		json,
		setRfNodes,
		setRfEdges,
		confirm
	]);

	useEffect(() => {
		handleDeleteSelectedRef.current = handleDeleteSelected;
	}, [handleDeleteSelected]);

	// (Dev-only zoom tuning logger removed in Move 01 — the
	// DEFAULT_ZOOM value is now settled at 1.056 and the
	// console spam is no longer earning its keep.)

	// Edits stream in from the editor panels via this handler.
	// Default behaviour is "update in place" (no remount).
	// Callers pass `{ remount: true }` for structural changes
	// (start-scene swap) where they want fitView to re-run.
	const handleJsonChange = useCallback(
		(next: AdventureJson, opts?: { remount?: boolean }) => {
			setJson(next);
			setDirty(true);
			if (opts?.remount) setJsonVersion((v) => v + 1);
		},
		[]
	);

	/**
	 * Drag-to-wire: user dragged a choice's source handle onto
	 * another node and released. Parse the choice index out of
	 * the source handle id (`c-${i}`), then rewire the choice's
	 * `next` in-place — no remount, so the edge re-routes
	 * immediately and the node selection stays put.
	 *
	 * Dropping onto the synthetic finish node sets `next: null`
	 * so the wire-up matches the schema's terminal semantics.
	 */
	const handleConnect = useCallback(
		(c: Connection) => {
			const i = Number(c.sourceHandle?.replace('c-', ''));
			if (!c.source || Number.isNaN(i)) return;
			const next = c.target === FINISH_NODE_ID ? null : c.target;
			setJson((prev) => updateChoice(prev, c.source!, i, { next }));
		},
		[]
	);

	/**
	 * Drop-to-create: user dragged a choice's source handle onto
	 * empty canvas and released. v12 surfaces this via
	 * `onConnectEnd` with a `FinalConnectionState` whose
	 * `isValid` is false (no node target). Spawn a fresh scene,
	 * wire the choice to it, pin the new scene at the cursor's
	 * drop coordinates so it lands where the author released
	 * (Move 02b Decision B -- we used to ignore the drop point
	 * because layoutGraph would yank the node back into the
	 * BFS grid; the new `pinnedPositions` parameter on
	 * layoutGraph lets us honour it now).
	 */
	const handleConnectEnd = useCallback(
		(event: MouseEvent | TouchEvent, conn: FinalConnectionState) => {
			if (conn.isValid) return; // onConnect already handled it
			const sourceId = conn.fromNode?.id;
			const i = Number(conn.fromHandle?.id?.replace('c-', ''));
			if (!sourceId || Number.isNaN(i)) return;

			// Translate the release point into flow-space BEFORE
			// awaiting the confirm modal, so the coordinates
			// reflect where the cursor actually was at drop time
			// (the user might pan after the modal opens).
			const clientX =
				'clientX' in event ? event.clientX : event.touches?.[0]?.clientX ?? 0;
			const clientY =
				'clientY' in event ? event.clientY : event.touches?.[0]?.clientY ?? 0;
			const dropPos = reactFlow.screenToFlowPosition({ x: clientX, y: clientY });

			// Drop-to-create can fire on accidental drags (release
			// too early, miss the target node). Confirm before
			// committing so a stray drag doesn't pollute the
			// adventure with empty stub scenes.
			const choiceLabel =
				json.scenes[sourceId]?.choices[i]?.label ?? '(choice)';
			void confirm({
				title: 'Create new scene',
				message: (
					<>
						Create a new scene wired up to <strong>{choiceLabel}</strong>{' '}
						(choice {i + 1} of <strong>{sourceId}</strong>)?
					</>
				),
				confirmLabel: 'Create scene',
				tone: 'primary'
			}).then((ok) => {
				if (!ok) return;
				setJson((prev) => {
					const { json: nextJson, id } = addSceneFromChoice(prev, sourceId, i);
					// Pin the new scene at the cursor's drop point so
					// the next layoutGraph pass doesn't snap it back
					// into the BFS layer. Offset by half the node width
					// so the cursor lands roughly at the node's centre.
					const NODE_WIDTH = 300;
					setManualPositions((curr) => {
						const next = new Map(curr);
						next.set(id, {
							x: dropPos.x - NODE_WIDTH / 2,
							y: dropPos.y - 30
						});
						return next;
					});
					// Defer the selection update so it lands after
					// the effect that rebuilds the graph --
					// otherwise the node we just created hasn't
					// been emitted yet and React Flow drops the
					// `selected` flag. We deliberately don't bump
					// jsonVersion here (we did pre-pin) because
					// that would remount + fitView and yank the
					// camera away from where the author dropped.
					queueMicrotask(() => setSelectedScene(id));
					return nextJson;
				});
			});
		},
		[json, confirm, reactFlow]
	);

	function loadJson(parsed: unknown) {
		if (
			!parsed ||
			typeof parsed !== 'object' ||
			!('start' in parsed) ||
			!('scenes' in parsed)
		) {
			setError('JSON is missing required fields (start, scenes).');
			return;
		}
		setJson(parsed as AdventureJson);
		setJsonVersion((v) => v + 1);
		setSelectedScene(null);
		setError(null);
		setDirty(false);
		clearDraft();
		clearHistory();
		// Externally-loaded JSON is not yet associated with a
		// localStorage entry; force a "Save as" on next save.
		setCurrentSaveName(null);
		setCurrentSaveId(null);
	}

	function loadExample() {
		// Default example -- the small "three paths, one
		// clearing" branching narrative. Neutral framing so the
		// structure is the lesson, not the theme.
		setJson(EXAMPLE_ADVENTURE);
		setJsonVersion((v) => v + 1);
		setSelectedScene(null);
		setError(null);
		setDirty(false);
		clearDraft();
		clearHistory();
		setCurrentSaveName(null);
		setCurrentSaveId(null);
	}

	function loadFoundryExample() {
		// Second example -- the themed seven-scene foundry
		// piece. Kept selectable because some authors used it
		// as a reference.
		setJson(FOUNDRY_EXAMPLE);
		setJsonVersion((v) => v + 1);
		setSelectedScene(null);
		setError(null);
		setDirty(false);
		clearDraft();
		clearHistory();
		setCurrentSaveName(null);
		setCurrentSaveId(null);
	}

	function newAdventure() {
		setJson(BLANK_ADVENTURE);
		setJsonVersion((v) => v + 1);
		setSelectedScene(null);
		setError(null);
		setCurrentSaveName(null);
		setCurrentSaveId(null);
		setDirty(false);
		clearDraft();
		clearHistory();
	}

	/**
	 * Boot overlay's "start from a skeleton" path. Loads the
	 * three-scene branching scaffold and pre-selects the start
	 * scene so the editor opens populated and the author can
	 * type straight into the entrance heading.
	 */
	function newFromSkeleton() {
		setJson(STARTER_ADVENTURE);
		setJsonVersion((v) => v + 1);
		setSelectedScene(STARTER_ADVENTURE.start);
		setError(null);
		setCurrentSaveName(null);
		setCurrentSaveId(null);
		setDirty(false);
		clearDraft();
		clearHistory();
	}

	/**
	 * Boot overlay's "take the tour" path. Loads the example,
	 * forces graph view, and preselects the start scene so the
	 * inline card is open — the tour's connection step points at
	 * a ✎ button inside that card, which only exists when a
	 * scene is selected. Then kicks off the coachmark sequence.
	 */
	function startTour() {
		setJson(EXAMPLE_ADVENTURE);
		setJsonVersion((v) => v + 1);
		handleViewModeChange('graph');
		setSelectedScene(EXAMPLE_ADVENTURE.start);
		setError(null);
		// Defer the tour to next tick so the boot overlay has
		// time to unmount and the canvas has rendered the new
		// nodes -- the tour's first step targets a SceneNode via
		// `data-tour="scene-node"` and won't find it otherwise.
		setTimeout(() => setShowTour(true), 50);
		setCurrentSaveName(null);
		setCurrentSaveId(null);
		setDirty(false);
		clearDraft();
		clearHistory();
	}

	/**
	 * Re-launch the tour on the CURRENT adventure (the ⋯ menu /
	 * ⌘K entry points). Unlike `startTour` it doesn't load the
	 * example — it runs on whatever's open. Forces graph view +
	 * selects the current start scene so the inline card is open
	 * for the tour's connection step (which targets a ✎ button
	 * that only exists when a scene is selected).
	 */
	function launchTour() {
		clearTourSeen();
		handleViewModeChange('graph');
		setSelectedScene(json.start);
		setShowTour(true);
	}

	/**
	 * Programmatic scene selection — closes any open modal AND
	 * highlights the named scene on the graph. Used by the
	 * Ship dialog's "jump to it" links on failing validation
	 * rows, and (eventually) by Move 02's validation surfaces.
	 *
	 * Updates both `selectedScene` (controls the right panel)
	 * AND the React Flow node `selected` flag (drives the
	 * visual highlight) — they're separate state in controlled
	 * mode and have to be synced manually.
	 */
	const handleJumpToScene = useCallback(
		(sceneId: string) => {
			setShowShipDialog(false);
			setSelectedScene(sceneId);
			setRfNodes((prev) =>
				prev.map((n) => ({ ...n, selected: n.id === sceneId }))
			);
		},
		[setRfNodes]
	);

	/**
	 * Save. Update-in-place if we already know which entry the
	 * current adventure came from; prompt for a name + create a
	 * new entry if not. The previous implementation always
	 * called `createSave`, which silently produced duplicate
	 * rows whenever the author saved the same adventure twice.
	 */
	async function saveCurrent() {
		// Have a tracked save and it still exists -> overwrite.
		if (currentSaveId && loadSave(currentSaveId)) {
			const ok = updateSave(currentSaveId, { json });
			if (!ok) {
				setError("Couldn't save to localStorage (quota or blocked).");
				return;
			}
			setDirty(false);
		clearDraft();
		clearHistory();
			return;
		}
		// Otherwise fall through to "save as".
		await saveAs();
	}

	async function saveAs() {
		const defaultName =
			currentSaveName ?? `Adventure ${new Date().toLocaleDateString()}`;
		const name = await prompt({
			title: 'Save as',
			message: 'Pick a name for this save. It will appear in the load dialog.',
			initialValue: defaultName,
			confirmLabel: 'Save'
		});
		if (!name) return;
		const id = createSave(name, json);
		if (id === null) {
			setError("Couldn't save to localStorage (quota or blocked).");
			return;
		}
		setCurrentSaveId(id);
		setCurrentSaveName(name);
		setDirty(false);
		clearDraft();
		clearHistory();
	}

	async function renameCurrent() {
		if (!currentSaveId) {
			// Nothing to rename yet. Treat as "save as" so the
			// menu entry never becomes a dead end.
			await saveAs();
			return;
		}
		const name = await prompt({
			title: 'Rename save',
			message: 'Choose a new name for this save.',
			initialValue: currentSaveName ?? '',
			confirmLabel: 'Rename'
		});
		if (!name) return;
		const ok = updateSave(currentSaveId, { name });
		if (!ok) {
			setError("Couldn't rename save (storage failure).");
			return;
		}
		setCurrentSaveName(name);
	}

	async function duplicateCurrent() {
		const suggested = currentSaveName
			? `${currentSaveName} (copy)`
			: `Adventure ${new Date().toLocaleDateString()} (copy)`;
		const name = await prompt({
			title: 'Duplicate save',
			message:
				'Create a copy of the current adventure under a new name. The original stays unchanged.',
			initialValue: suggested,
			confirmLabel: 'Duplicate'
		});
		if (!name) return;
		const id = createSave(name, json);
		if (id === null) {
			setError("Couldn't duplicate save (quota or blocked).");
			return;
		}
		setCurrentSaveId(id);
		setCurrentSaveName(name);
		setDirty(false);
		clearDraft();
		clearHistory();
	}

	function loadFromStorage(loadedJson: AdventureJson, name: string, id: string) {
		setJson(loadedJson);
		setJsonVersion((v) => v + 1);
		setDirty(false);
		clearDraft();
		clearHistory();
		setSelectedScene(null);
		setError(null);
		setCurrentSaveName(name);
		setCurrentSaveId(id);
	}

	// Command list for the ⌘K palette. Every entry routes to a
	// handler already wired elsewhere -- the palette is a
	// universal dispatcher, not a parallel API. Disabled
	// commands are filtered out inside the palette so the user
	// can't pick something that wouldn't work.
	const paletteCommands: PaletteCommand[] = [
		{ id: 'new', icon: '＋', label: 'New adventure', kbd: '⌘N', onRun: newAdventure },
		{
			id: 'save',
			icon: '✓',
			label: 'Save',
			kbd: '⌘S',
			disabled: !saveAvailable,
			onRun: () => void saveCurrent()
		},
		{
			id: 'saveAs',
			icon: '⤓',
			label: 'Save as…',
			kbd: '⌘⇧S',
			disabled: !saveAvailable,
			onRun: () => void saveAs()
		},
		{
			id: 'rename',
			icon: '✎',
			label: 'Rename save…',
			disabled: !saveAvailable || currentSaveId === null,
			onRun: () => void renameCurrent()
		},
		{
			id: 'duplicate',
			icon: '⎘',
			label: 'Duplicate save…',
			disabled: !saveAvailable,
			onRun: () => void duplicateCurrent()
		},
		{
			id: 'load',
			icon: '⌘',
			label: 'Load saved…',
			disabled: !saveAvailable,
			onRun: () => setShowLoadDialog(true)
		},
		{
			id: 'ship',
			icon: '↗',
			label: 'Export / Ship…',
			kbd: '⌘E',
			onRun: () => setShowShipDialog(true)
		},
		// Open menu items
		{ id: 'openExample', icon: '◆', label: 'Open: example adventure', onRun: loadExample },
		{
			id: 'openFoundry',
			icon: '◇',
			label: 'Open: the foundry (example 2)',
			onRun: loadFoundryExample
		},
		// Edit
		{
			id: 'undo',
			icon: '↶',
			label: 'Undo',
			kbd: '⌘Z',
			disabled: history.past.length === 0,
			onRun: undo
		},
		{
			id: 'redo',
			icon: '↷',
			label: 'Redo',
			kbd: '⌘⇧Z',
			disabled: history.future.length === 0,
			onRun: redo
		},
		// View
		{
			id: 'settings',
			icon: '⚙',
			label: 'Adventure settings…',
			onRun: () => setShowSettings(true)
		},
		{
			id: 'play',
			icon: '▶',
			label: 'Show playtest (graph view)',
			onRun: () => handleViewModeChange('graph')
		},
		{
			id: 'viewGraph',
			icon: '◯',
			label: 'View: graph',
			disabled: viewMode === 'graph',
			onRun: () => handleViewModeChange('graph')
		},
		{
			id: 'viewWrite',
			icon: '✎',
			label: 'View: write (script)',
			disabled: viewMode === 'write',
			onRun: () => handleViewModeChange('write')
		},
		{
			id: 'viewSplit',
			icon: '◧',
			label: 'View: split',
			disabled: viewMode === 'split',
			onRun: () => handleViewModeChange('split')
		},
		{
			id: 'flowH',
			icon: '→',
			label: 'Flow direction: horizontal',
			disabled: flowDirection === 'horizontal',
			onRun: () => handleFlowDirectionChange('horizontal')
		},
		{
			id: 'flowV',
			icon: '↓',
			label: 'Flow direction: vertical',
			disabled: flowDirection === 'vertical',
			onRun: () => handleFlowDirectionChange('vertical')
		},
		// Layout / camera
		{ id: 'autoLayout', icon: '⤢', label: 'Auto layout', kbd: '⇧L', onRun: handleAutoLayout },
		{
			id: 'resetZoom',
			icon: '⊡',
			label: 'Reset zoom',
			onRun: () =>
				reactFlow.fitView({
					padding: 0.15,
					duration: 400,
					minZoom: DEFAULT_ZOOM,
					maxZoom: DEFAULT_ZOOM
				})
		},
		{
			id: 'fit',
			icon: '⛶',
			label: 'Fit to screen',
			onRun: () => reactFlow.fitView({ padding: 0.12, duration: 400 })
		},
		// Help
		{
			id: 'tour',
			icon: '?',
			label: 'Help / tour',
			onRun: launchTour
		},
		{ id: 'about', icon: 'ⓘ', label: 'About…', onRun: () => setShowAbout(true) }
	];

	return (
		<SceneActionsProvider value={{ onPlayFromHere: handlePlayFromHere }}>
		<div
			style={{
				height: '100vh',
				display: 'flex',
				flexDirection: 'column',
				background: VOID,
				color: '#eef0f5'
			}}
		>
			{showBoot && (
				<BootOverlay
					onClose={() => setShowBoot(false)}
					onTour={startTour}
					onSkeleton={newFromSkeleton}
					onLoadJson={loadJson}
					onError={setError}
				/>
			)}

			<Toolbar
				documentName={currentSaveName ?? '(unsaved)'}
				dirty={dirty}
				stats={{ scenes: Object.keys(json.scenes).length, maxScore }}
				onLoadExample={loadExample}
				onLoadFoundryExample={loadFoundryExample}
				onLoadJson={loadJson}
				onNewAdventure={newAdventure}
				onSave={saveCurrent}
				onOpenLoadDialog={() => setShowLoadDialog(true)}
				onOpenShipDialog={() => setShowShipDialog(true)}
				onPlay={() => handleViewModeChange('graph')}
				onOpenSettings={() => setShowSettings(true)}
				onAutoLayout={handleAutoLayout}
				onResetZoom={() => {
					// Snap back to the canonical zoom on the start +
					// successors -- the same framing the canvas had
					// on its very first render. Useful when the user
					// has zoomed deep or panned far and wants the
					// "home" view back without losing layout.
					reactFlow.fitView({
						padding: 0.15,
						duration: 400,
						minZoom: DEFAULT_ZOOM,
						maxZoom: DEFAULT_ZOOM
					});
				}}
				onFitToScreen={() => {
					// Fit the entire graph (all nodes) to the
					// viewport, no zoom constraint. The Move 06
					// spec listed this distinct from Reset zoom:
					// reset = back to canonical framing, fit-to-
					// screen = "show me everything at once."
					reactFlow.fitView({
						padding: 0.12,
						duration: 400
					});
				}}
				onStartTour={launchTour}
				onShowAbout={() => setShowAbout(true)}
				onOpenPalette={() => setShowPalette(true)}
				onUndo={undo}
				onRedo={redo}
				canUndo={history.past.length > 0}
				canRedo={history.future.length > 0}
				onSaveAs={saveAs}
				onRename={renameCurrent}
				onDuplicate={duplicateCurrent}
				hasCurrentSave={currentSaveId !== null}
				saveAvailable={saveAvailable}
				onError={setError}
			/>

			{showTour && (
				<Tour
					onStepActive={handleTourStep}
					onClose={() => {
						setShowTour(false);
						// Leave the canvas tidy — the tour drove
						// selection to demo each surface; drop it on exit.
						clearAllSelection();
					}}
				/>
			)}
			{showAbout && <AboutDialog onClose={() => setShowAbout(false)} />}
			{showSettings && (
				<AdventureSettingsDialog
					json={json}
					maxScore={maxScore}
					onJsonChange={handleJsonChange}
					onSelectScene={setSelectedScene}
					onClose={() => setShowSettings(false)}
				/>
			)}
			{showPalette && (
				<CommandPalette
					commands={paletteCommands}
					onClose={() => setShowPalette(false)}
				/>
			)}
			{draftRestored !== null && (
				<DraftBanner
					savedAt={draftRestored}
					onDismiss={() => setDraftRestored(null)}
				/>
			)}

			{/* View-mode sub-bar — sits beneath the toolbar so
			    it's always visible without competing with the
			    load / save / ship actions for toolbar real
			    estate. Stats on the left double as a glance at
			    the document's shape. */}
			<div
				style={{
					display: 'flex',
					alignItems: 'center',
					justifyContent: 'space-between',
					padding: '6px 18px',
					background: PANEL,
					borderBottom: `1px solid ${PANEL_BORDER}`,
					fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
					fontSize: 10,
					color: DIM
				}}
			>
				<span>
					~/adventure ·{' '}
					<span style={{ color: PHOSPHOR }}>
						{Object.keys(json.scenes).length}
					</span>{' '}
					scenes ·{' '}
					<span style={{ color: PHOSPHOR }}>
						{Object.values(json.scenes).reduce((n, s) => n + s.choices.length, 0)}
					</span>{' '}
					choices · max <span style={{ color: PHOSPHOR }}>{maxScore}</span>
				</span>
				<div style={{ display: 'flex', alignItems: 'center', gap: 10 }}>
					<FlowDirectionToggle
						direction={flowDirection}
						onChange={handleFlowDirectionChange}
					/>
					<ViewToggle mode={viewMode} onChange={handleViewModeChange} />
				</div>
			</div>

			{showShipDialog && (
				<ShipDialog
					json={json}
					onClose={() => setShowShipDialog(false)}
					onJumpToScene={handleJumpToScene}
				/>
			)}

			{showLoadDialog && (
				<LoadDialog
					onClose={() => setShowLoadDialog(false)}
					onLoad={loadFromStorage}
				/>
			)}

			<Toast message={error} onDismiss={() => setError(null)} />

			<MarkerDefs />

			{/* Inline scene editor only makes sense when the
			    graph is on-screen — it anchors to a node's
			    screen coords via the React Flow instance. Hidden
			    in write mode. */}
			{viewMode !== 'write' && selectedScene && (
				<InlineSceneEditor
					json={json}
					sceneId={selectedScene}
					onJsonChange={handleJsonChange}
					onClose={() => {
						setSelectedScene(null);
						setRfNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
					}}
					onPlayFromHere={() => handlePlayFromHere(selectedScene)}
					onEditConnection={(choiceIndex) =>
						handleEditConnection(selectedScene, choiceIndex)
					}
					onSceneDeleted={() => {
						setSelectedScene(null);
						setRfNodes((prev) => prev.map((n) => ({ ...n, selected: false })));
					}}
				/>
			)}

			{/* Edge editor — floats over the canvas when a
			    connection is selected. Anchored to the edge's
			    midpoint via the React Flow instance, so it sits
			    on the wire the author just clicked. Carries the
			    choice's full surface: label, points, next, and
			    REACTION (the field the engine still calls
			    `flavour`). */}
			{viewMode !== 'write' &&
				selectedEdgeId &&
				(() => {
					const edge = rfEdges.find((e) => e.id === selectedEdgeId);
					const data = edge?.data as
						| { sceneId?: string; choiceIndex?: number }
						| undefined;
					if (!edge || data?.sceneId === undefined || data.choiceIndex === undefined) {
						return null;
					}
					return (
						<EdgeEditor
							json={json}
							sceneId={data.sceneId}
							choiceIndex={data.choiceIndex}
							onJsonChange={handleJsonChange}
							onClose={() => {
								setRfEdges((prev) => prev.map((e) => ({ ...e, selected: false })));
								setSelectedEdgeId(null);
							}}
						/>
					);
				})()}

			<div style={{ flex: 1, display: 'flex', minHeight: 0 }}>
				{/* ScriptView occupies the full canvas area in
				    write mode and the right half in split. The
				    graph canvas + right panel render in graph and
				    split modes (right panel only in graph — split
				    drops it to make room for the script). */}
				{viewMode === 'write' && (
					<ScriptView
						json={json}
						maxScore={maxScore}
						selectedSceneId={selectedScene}
						flowDirection={flowDirection}
						onJsonChange={handleJsonChange}
						onSelectScene={setSelectedScene}
						onPlayFromHere={(sceneId) => {
							handlePlayFromHere(sceneId);
							// In write mode there's no right panel,
							// so flip to graph so the Terminal that
							// "play from here" just booted is visible.
							handleViewModeChange('graph');
						}}
					/>
				)}

				{(viewMode === 'graph' || viewMode === 'split') && (
				<div style={{ flex: 1, position: 'relative' }}>
					<ReactFlow
						key={jsonVersion}
						nodes={rfNodes}
						edges={rfEdges}
						onNodesChange={onNodesChange}
						onEdgesChange={onEdgesChange}
						onConnect={handleConnect}
						onConnectEnd={handleConnectEnd}
						onNodeClick={handleNodeClick}
						onNodeDragStop={handleNodeDragStop}
						onEdgeClick={handleEdgeClick}
						// Disable React Flow's built-in deletion key —
						// it would remove the node from the rfNodes
						// state without touching `json`, so the next
						// rebuild would resurrect it. Our own Delete /
						// Backspace handler (in App's useEffect) goes
						// through `deleteScene` instead so json stays
						// the source of truth.
						deleteKeyCode={null}
						connectionLineStyle={{
							stroke: PHOSPHOR,
							strokeWidth: 2,
							strokeDasharray: '2 5'
						}}
						nodeTypes={nodeTypes}
						edgeTypes={edgeTypes}
						onSelectionChange={handleSelectionChange}
						fitView
						fitViewOptions={{
							nodes: focusNodeIds.map((id) => ({ id })),
							padding: 0.15,
							// Both min and max set to DEFAULT_ZOOM so
							// fitView can't pick anything else — the
							// "natural" fit for start + successors is
							// usually higher than what reads well, so we
							// clamp from above as well as below. The
							// user-zoom range below stays wide so they can
							// still zoom out/in via the controls.
							minZoom: DEFAULT_ZOOM,
							maxZoom: DEFAULT_ZOOM
						}}
						minZoom={CANVAS_MIN_ZOOM}
						maxZoom={CANVAS_MAX_ZOOM}
						proOptions={{ hideAttribution: true }}
						style={{ background: VOID }}
					>
						<Background
							color={PANEL_BORDER}
							variant={BackgroundVariant.Dots}
							gap={20}
							size={1}
						/>
						<Controls
							position="top-right"
							style={{ background: PANEL, border: `1px solid ${PANEL_BORDER}` }}
						/>
						<MiniMap
							style={{
								background: PANEL,
								border: `1px solid ${PANEL_BORDER}`
							}}
							maskColor="rgba(10, 10, 15, 0.7)"
							nodeColor={(n) =>
								n.id === FINISH_NODE_ID ? MAGENTA : PHOSPHOR
							}
							nodeStrokeColor={PANEL_BORDER}
						/>
					</ReactFlow>

					{/* Auto-layout button — top-left so it stays
					    out of the way of React Flow's built-in
					    Controls at top-right. Tidies the canvas
					    after manual node-dragging by re-running
					    the BFS layout and fitting the view. */}
					<button
						onClick={handleAutoLayout}
						title="Re-run auto layout (Shift+L)"
						style={{
							position: 'absolute',
							top: 12,
							left: 12,
							background: PANEL,
							color: AMBER,
							border: `1px solid ${PANEL_BORDER}`,
							borderRadius: 5,
							padding: '5px 10px',
							fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
							fontSize: 11,
							cursor: 'pointer',
							transition: 'border-color 120ms, color 120ms, background 120ms'
						}}
						onMouseEnter={(e) => {
							e.currentTarget.style.borderColor = AMBER;
							e.currentTarget.style.background = `${AMBER}11`;
						}}
						onMouseLeave={(e) => {
							e.currentTarget.style.borderColor = PANEL_BORDER;
							e.currentTarget.style.background = PANEL;
						}}
					>
						⤢ auto layout
					</button>

					<div
						style={{
							position: 'absolute',
							bottom: 16,
							left: 16,
							color: DIM,
							fontSize: 10,
							fontFamily: 'ui-monospace, "JetBrains Mono", monospace',
							pointerEvents: 'none'
						}}
					>
						scroll to zoom · drag to pan · click to select · click again to
						deselect · shift-click or shift-drag for multi-select · Del to
						delete · Shift+L to tidy
					</div>

					{/* Restore-card chip — visible only when the user
					    expanded the inline editor into the side panel.
					    Lets them bring the floating card back without
					    losing selection. Bottom-right so it's out of the
					    way of the bottom-left controls hint. */}
					{/* Floating validation summary -- magenta chip
					    counting unreachable / dead-end / missing-
					    target issues. Opens the ship dialog (which
					    has the full breakdown) on click. */}
					<ValidationNote
						validation={validation}
						onOpenShipDialog={() => setShowShipDialog(true)}
					/>

				</div>
				)}

				{/* Split mode — second half hosts the ScriptView.
				    Border on its left separates it visually from
				    the graph. Right panel is omitted in split to
				    make room (per the design handoff). */}
				{viewMode === 'split' && (
					<div
						style={{
							flex: 1,
							display: 'flex',
							borderLeft: `1px solid ${PANEL_BORDER}`,
							minWidth: 0
						}}
					>
						<ScriptView
							json={json}
							maxScore={maxScore}
							selectedSceneId={selectedScene}
							flowDirection={flowDirection}
							onJsonChange={handleJsonChange}
							onSelectScene={setSelectedScene}
							onPlayFromHere={(sceneId) => {
								handlePlayFromHere(sceneId);
								handleViewModeChange('graph');
							}}
						/>
					</div>
				)}

				{/* RightPanel rides alongside only in graph mode.
				    Split mode replaces it with the ScriptView half;
				    write mode is full-document. */}
				{viewMode === 'graph' && (
					<RightPanel
						json={json}
						jsonVersion={jsonVersion}
						playFrom={playFrom}
						playRequestId={playRequestId}
						onPlayStateChange={setPlayState}
						onClearPlayFrom={() => setPlayFrom(null)}
					/>
				)}
			</div>
		</div>
		</SceneActionsProvider>
	);
}
